/**
 * App's global ErrorBoundary. A render failure on any route must not
 * leave the screen blank: it is caught here and a neutral fallback is shown
 * with retry and home actions. Theme-aware (tokens) and with console logging.
 */

import { type Component, ErrorBoundary, type JSX } from "solid-js";
import { AlertTriangle, Home, RotateCcw } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";

interface PlayErrorBoundaryProps {
  children: JSX.Element;
  /** Identifies the subtree that failed for the log. */
  scope?: string;
}

export const PlayErrorBoundary: Component<PlayErrorBoundaryProps> = (props) => {
  const { t } = useI18n();
  return (
    <ErrorBoundary
      fallback={(error, reset) => {
        console.error(`[PlayErrorBoundary${props.scope ? `:${props.scope}` : ""}]`, error);
        return (
          <div
            role="alert"
            aria-live="assertive"
            class="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background p-6 text-center"
          >
            <div class="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle class="h-8 w-8 text-destructive" />
            </div>
            <div class="space-y-2">
              <h2 class="text-2xl font-bold tracking-tight text-foreground">
                {t(msg`Something went wrong`)}
              </h2>
              <p class="mx-auto max-w-sm text-sm text-muted-foreground">
                {t(msg`An unexpected error has occurred.`)}
              </p>
            </div>
            <div class="flex flex-col items-center gap-3 sm:flex-row">
              <Button
                onClick={() => {
                  reset();
                }}
                class="gap-2"
              >
                <RotateCcw class="h-4 w-4" />
                {t(msg`Try again`)}
              </Button>
              <a href="/">
                <Button variant="outline" class="gap-2">
                  <Home class="h-4 w-4" />
                  {t(msg`Home`)}
                </Button>
              </a>
            </div>
          </div>
        );
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
};
